import { ArrowRight, CircleDot, CircleUserRound, Sparkles, WandSparkles } from "lucide-react";
import { Link } from "react-router";
import { FormattedMessage } from "react-intl";

import type { AssistantOnboardingCompletionReference } from "@/lib/assistant-onboarding-completion";
import { cn } from "@/lib/utils";
import type { BrandAssistantCreationPreviewToolName } from "@runcheetah/shared";
import { Button } from "@runcheetah/ui";

import { AssistantInlineStatus } from "./assistant-inline-status";

const liveProgressCompletionKinds: ReadonlySet<AssistantOnboardingCompletionReference["kind"]> =
  new Set(["brand_scan_in_progress", "strategy_generation_in_progress"]);

const brandScanProgressDetails = [
  <FormattedMessage
    key="extractingVisualUniverse"
    id="assistantOnboardingCompletion.progress.extractingVisualUniverse"
    defaultMessage="Extracting your visual universe..."
  />,
  <FormattedMessage
    key="readingBrandSignals"
    id="assistantOnboardingCompletion.progress.readingBrandSignals"
    defaultMessage="Reading the strongest brand signals..."
  />,
  <FormattedMessage
    key="mappingAudience"
    id="assistantOnboardingCompletion.progress.mappingAudience"
    defaultMessage="Mapping your audience and positioning..."
  />,
  <FormattedMessage
    key="shapingFoundations"
    id="assistantOnboardingCompletion.progress.shapingFoundations"
    defaultMessage="Shaping your Brand Kit foundations..."
  />,
  <FormattedMessage
    key="stillWorking"
    id="assistantOnboardingCompletion.progress.stillWorking"
    defaultMessage="Still working, this can take a moment..."
  />,
] as const;

const strategyProgressDetails = [
  <FormattedMessage
    key="turningProfileIntoStrategy"
    id="assistantOnboardingCompletion.progress.turningProfileIntoStrategy"
    defaultMessage="Turning your brand profile into strategy..."
  />,
  <FormattedMessage
    key="shapingCampaignDirection"
    id="assistantOnboardingCompletion.progress.shapingCampaignDirection"
    defaultMessage="Shaping the first campaign direction..."
  />,
  <FormattedMessage
    key="preparingLaunchContext"
    id="assistantOnboardingCompletion.progress.preparingLaunchContext"
    defaultMessage="Preparing your launch-ready context..."
  />,
  <FormattedMessage
    key="wrappingFinalSteps"
    id="assistantOnboardingCompletion.progress.wrappingFinalSteps"
    defaultMessage="Wrapping up the final setup steps..."
  />,
  <FormattedMessage
    key="almostDone"
    id="assistantOnboardingCompletion.progress.almostDone"
    defaultMessage="Almost done..."
  />,
] as const;

const fallbackProgressDetails = [
  <FormattedMessage
    key="fallbackStillWorking"
    id="assistantOnboardingCompletion.progress.fallbackStillWorking"
    defaultMessage="Still working..."
  />,
  <FormattedMessage
    key="fallbackKeepingMoving"
    id="assistantOnboardingCompletion.progress.fallbackKeepingMoving"
    defaultMessage="Keeping your setup moving..."
  />,
  <FormattedMessage
    key="fallbackCheckingPieces"
    id="assistantOnboardingCompletion.progress.fallbackCheckingPieces"
    defaultMessage="Checking the last pieces..."
  />,
  <FormattedMessage
    key="fallbackAlmostDone"
    id="assistantOnboardingCompletion.progress.fallbackAlmostDone"
    defaultMessage="Almost done..."
  />,
] as const;

const ONBOARDING_PROGRESS_THOUGHT_INTERVAL_MS = 3_000;

export function AssistantOnboardingCompletionCards({
  completions,
  pulseHref,
  isPromptDisabled = false,
  onPrompt,
}: {
  completions: AssistantOnboardingCompletionReference[];
  pulseHref?: string;
  isPromptDisabled?: boolean;
  onPrompt?: (prompt: string, creationTool: BrandAssistantCreationPreviewToolName | null) => void;
}) {
  if (completions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2.5">
      {completions.map((completion) => (
        <OnboardingCompletionMessage
          key={`${completion.kind}-${completion.generatedAt}`}
          completion={completion}
          pulseHref={pulseHref}
          isPromptDisabled={isPromptDisabled}
          onPrompt={onPrompt}
        />
      ))}
    </div>
  );
}

function OnboardingCompletionMessage({
  completion,
  pulseHref,
  isPromptDisabled,
  onPrompt,
}: {
  completion: AssistantOnboardingCompletionReference;
  pulseHref?: string;
  isPromptDisabled: boolean;
  onPrompt?: (prompt: string, creationTool: BrandAssistantCreationPreviewToolName | null) => void;
}) {
  if (liveProgressCompletionKinds.has(completion.kind)) {
    return <OnboardingProgressThought kind={completion.kind} />;
  }

  const ctaPrompt = completion.ctaPrompt?.trim() || null;
  const ctaLabel = completion.ctaLabel?.trim() || null;
  const meta = getCompletionMeta(completion.kind);
  const toneClass = meta.isBackground ? "border-info/30" : "border-muted-foreground/20";

  return (
    <div className={cn("pl-3", toneClass, "border-l-2")}>
      <div className="space-y-2">
        <div className="flex items-start gap-2.5">
          <span
            className={cn(
              "mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full",
              meta.iconBg,
            )}
          >
            <meta.icon className={cn("h-3.5 w-3.5", meta.iconColor)} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1 space-y-1">
            <div className="text-sm font-semibold text-foreground">{completion.title}</div>
            <p className="text-sm leading-6 text-muted-foreground">{completion.summary}</p>
          </div>
        </div>

        {ctaLabel && ctaPrompt ? (
          <CompletionCta
            label={ctaLabel}
            prompt={ctaPrompt}
            creationTool={getOnboardingCompletionCreationTool(completion.kind)}
            pulseHref={pulseHref}
            isDisabled={isPromptDisabled}
            onPrompt={onPrompt}
          />
        ) : null}
      </div>
    </div>
  );
}

export function getOnboardingCompletionCreationTool(
  kind: AssistantOnboardingCompletionReference["kind"],
): BrandAssistantCreationPreviewToolName | null {
  return kind === "first_asset_cta" ? "preview_standalone_asset_batch_creation" : null;
}

function OnboardingProgressThought({
  kind,
}: {
  kind: AssistantOnboardingCompletionReference["kind"];
}) {
  const meta = getProgressThoughtMeta(kind);

  return (
    <AssistantInlineStatus
      details={meta.details}
      className="max-w-[46rem]"
      intervalMs={ONBOARDING_PROGRESS_THOUGHT_INTERVAL_MS}
    />
  );
}

function getProgressThoughtMeta(kind: AssistantOnboardingCompletionReference["kind"]) {
  if (kind === "brand_scan_in_progress") {
    return {
      details: brandScanProgressDetails,
    };
  }

  if (kind === "strategy_generation_in_progress") {
    return {
      details: strategyProgressDetails,
    };
  }

  return {
    details: fallbackProgressDetails,
  };
}

function CompletionCta({
  label,
  prompt,
  creationTool,
  pulseHref,
  isDisabled,
  onPrompt,
}: {
  label: string;
  prompt: string;
  creationTool: BrandAssistantCreationPreviewToolName | null;
  pulseHref?: string;
  isDisabled: boolean;
  onPrompt?: (prompt: string, creationTool: BrandAssistantCreationPreviewToolName | null) => void;
}) {
  if (onPrompt) {
    return (
      <Button
        type="button"
        size="sm"
        className="rounded-full"
        disabled={isDisabled}
        onClick={() => onPrompt(prompt, creationTool)}
      >
        {label}
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Button>
    );
  }

  if (pulseHref) {
    return (
      <Button asChild size="sm" className="rounded-full">
        <Link to={pulseHref}>
          {label}
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </Link>
      </Button>
    );
  }

  return (
    <p className="text-xs font-medium text-muted-foreground">
      <FormattedMessage
        id="assistantOnboardingCompletion.openPulseHint"
        defaultMessage="Open Pulse to generate the first asset."
      />
    </p>
  );
}

function getCompletionMeta(kind: AssistantOnboardingCompletionReference["kind"]) {
  switch (kind) {
    case "strategy_ready":
      return {
        icon: Sparkles,
        iconBg: "bg-primary/14",
        iconColor: "text-primary",
        isBackground: false,
      };
    case "profile_updates_pending":
    case "profile_updates_resolved":
      return {
        icon: WandSparkles,
        iconBg: "bg-amber-500/12",
        iconColor: "text-amber-700 dark:text-amber-300",
        isBackground: false,
      };
    case "first_asset_pending":
      return {
        icon: CircleDot,
        iconBg: "bg-info/10",
        iconColor: "text-info",
        isBackground: false,
      };
    case "background_complete":
      return {
        icon: CircleUserRound,
        iconBg: "bg-emerald-500/12",
        iconColor: "text-emerald-700 dark:text-emerald-300",
        isBackground: true,
      };
    case "first_asset_cta":
      return {
        icon: Sparkles,
        iconBg: "bg-primary/14",
        iconColor: "text-primary",
        isBackground: true,
      };
    default:
      return {
        icon: Sparkles,
        iconBg: "bg-primary/12",
        iconColor: "text-primary",
        isBackground: false,
      };
  }
}
